import { PlatformLink } from "./PlatformLink";
import { DealKindPicker } from "./DealKindPicker";
import React, { useState } from "react";
import { useDeal } from "../lib/store";
import { parseApi, streamPost, streamPostForm } from "../lib/api";
import { mapFields, parseLoose, num } from "../lib/useFinancialParse";
import { Card, Tile } from "./shared";
import { fmt$, isRecap } from "../lib/model";
import { DeckImport } from "./DeckImport";
import { Upload, FileText, Table2, CheckCircle2, Receipt } from "lucide-react";
import { toast } from "sonner";

function DropZone({ id, title, accept, sizeNote, Icon, onFile, doc, multiple }) {
  const [over, setOver] = useState(false);
  const inputRef = React.useRef(null);
  const handleFiles = (list) => { Array.from(list || []).forEach((f) => onFile(f)); };
  return (
    <Card title={title}>
      <div
        className={`drop ${over ? "over" : ""}`}
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setOver(true); }}
        onDragLeave={() => setOver(false)}
        onDrop={(e) => { e.preventDefault(); setOver(false); handleFiles(e.dataTransfer.files); }}
        data-testid={`drop-${id}`}
      >
        <div className="icon"><Icon size={28} /></div>
        <div className="t">Drop file{multiple ? "s" : ""} or click to browse</div>
        <div className="s">{sizeNote}</div>
        <input ref={inputRef} type="file" accept={accept} multiple={multiple} style={{ display: "none" }} onChange={(e) => handleFiles(e.target.files)} data-testid={`input-${id}`} />
      </div>
      {doc && (
        <div style={{ marginTop: 12, fontSize: 12, display: "flex", alignItems: "center", gap: 8 }} data-testid={`status-${id}`}>
          {doc.status === "parsed" ? <CheckCircle2 size={16} color="#4caf7d" /> : null}
          <span>{doc.name} · {(doc.size / 1024).toFixed(0)} KB · </span>
          <span className={doc.status === "parsed" ? "pos" : doc.status === "error" ? "neg" : "neu"}>{doc.status === "parsing" ? "Processing…" : doc.status === "parsed" ? "✓ Parsed" : doc.status === "error" ? "Error" : ""}</span>
        </div>
      )}
    </Card>
  );
}

export function UploadParsePanel() {
  const { M, patch, setDealName, requestSave } = useDeal();
  const [docs, setDocs] = useState({});
  const [fields, setFields] = useState(null);
  const [busy, setBusy] = useState(false);
  const [taxDocs, setTaxDocs] = useState([]);

  const handle = async (kind, file) => {
    setDocs((d) => ({ ...d, [kind]: { name: file.name, size: file.size, status: "parsing" } }));
    setBusy(true);
    try {
      const isPdf = file.name.toLowerCase().endsWith(".pdf");
      let full = "";
      let meta = { filename: file.name };
      const fd = new FormData();
      fd.append("file", file);
      if (isPdf) {
        await streamPostForm("/parse/analyze-pdf", fd, (chunk, f) => { full = f; });
      } else {
        const ex = await parseApi.extractText(fd);
        meta = ex;
        await streamPost("/parse/analyze", { text: ex.text, kind: ex.kind }, (chunk, f) => { full = f; });
      }
      const parsed = parseLoose(full);
      const mapped = mapFields(parsed || {});
      patch(mapped);
      if (mapped.dealName) setDealName(mapped.dealName);
      setTimeout(requestSave, 50);
      setFields({ raw: parsed, mapped, source: file.name });
      setDocs((d) => ({ ...d, [kind]: { name: file.name, size: file.size, status: "parsed", meta } }));
      toast.success(`✓ Auto-populated ${Object.keys(mapped).length} fields from ${file.name}`);
    } catch (e) {
      setDocs((d) => ({ ...d, [kind]: { name: file.name, size: file.size, status: "error" } }));
      toast.error(e?.response?.data?.detail || "Parse failed — the file may be unreadable");
    } finally {
      setBusy(false);
    }
  };

  const handleTax = async (file) => {
    if (!file.name.toLowerCase().endsWith(".pdf")) { toast.error("Tax returns must be PDF"); return; }
    const id = `${file.name}-${Date.now()}`;
    setTaxDocs((t) => [...t, { id, name: file.name, status: "parsing", years: [] }]);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("mode", "tax");
      let full = "";
      await streamPostForm("/parse/analyze-pdf", fd, (chunk, f) => { full = f; });
      const parsed = parseLoose(full) || {};
      const years = (parsed.taxYears || []).map((y) => ({
        year: String(y.year ?? ""), revenue: num(y.revenue) || 0, netIncome: num(y.netIncome) || 0,
        officerComp: num(y.officerComp) || 0, depreciation: num(y.depreciation) || 0, interest: num(y.interest) || 0,
        taxSDE: num(y.taxSDE) || 0, taxEBITDA: num(y.taxEBITDA) || 0,
      }));
      setTaxDocs((t) => t.map((d) => d.id === id ? { ...d, status: "parsed", years, taxSDE: num(parsed.taxSDE), taxEBITDA: num(parsed.taxEBITDA) } : d));
      if (years.length) {
        const byYear = Object.fromEntries((M.taxYears || []).map((y) => [String(y.year), y]));
        years.forEach((y) => { byYear[y.year] = { ...y, source: file.name }; });
        patch({ taxYears: Object.values(byYear).sort((a, b) => String(a.year).localeCompare(String(b.year))) });
        setTimeout(requestSave, 50);
      }
      toast.success(`✓ Parsed tax return: ${file.name}${years.length ? ` — ${years.length} year${years.length === 1 ? "" : "s"} saved to the model` : ""}`);
    } catch (e) {
      setTaxDocs((t) => t.map((d) => d.id === id ? { ...d, status: "error" } : d));
      toast.error("Tax return parse failed");
    }
  };

  const allTaxYears = taxDocs.flatMap((d) => d.years || []).filter((y) => y.taxSDE);
  const latestTax = allTaxYears.length ? allTaxYears.reduce((a, b) => (String(b.year) > String(a.year) ? b : a)) : null;
  const taxSDE = latestTax ? latestTax.taxSDE : (taxDocs.find((d) => d.taxSDE)?.taxSDE);
  const taxEBITDA = latestTax ? latestTax.taxEBITDA : (taxDocs.find((d) => d.taxEBITDA)?.taxEBITDA);
  const sdeDiff = taxSDE ? M.sde - taxSDE : null;
  const sdeDiffPct = taxSDE ? ((M.sde - taxSDE) / taxSDE) * 100 : null;

  return (
    <div data-testid="upload-parse-panel">
      <div className="ra-sub" style={{ marginBottom: 10 }}>AI extraction auto-populates the model — upload CIMs, financials and tax returns (multiple files supported).</div>

      <DealKindPicker />

      {isRecap(M) && <div style={{ marginTop: 12 }}><DeckImport /></div>}

      <div className="grid g3">
        <DropZone id="doc" title="CIM / Offering Memorandum" accept=".pdf,.docx" sizeNote="PDF or DOCX · multiple OK" Icon={FileText} multiple onFile={(f) => handle("doc", f)} doc={docs.doc} />
        <DropZone id="xls" title="Financial Statements" accept=".xlsx,.xls,.csv" sizeNote="XLSX / XLS / CSV · multiple OK" Icon={Table2} multiple onFile={(f) => handle("xls", f)} doc={docs.xls} />
        <DropZone id="tax" title="Tax Returns" accept=".pdf" sizeNote="PDF (1120/1120-S/1065/Sch C) · multiple years" Icon={Receipt} multiple onFile={handleTax} doc={taxDocs[taxDocs.length - 1]} />
      </div>

      {taxDocs.length > 0 && (
        <Card title="SDE / EBITDA Reconciliation — Seller-Adjusted vs Tax Returns" style={{ marginTop: 12 }} className="table-scroll" testid="tax-reconciliation">
          <div className="grid g4" style={{ marginBottom: 12 }}>
            <Tile label="Seller-adjusted SDE" value={fmt$(M.sde)} />
            <Tile label={`Tax-return SDE ${latestTax ? `(${latestTax.year})` : ""}`} value={taxSDE ? fmt$(taxSDE) : "—"} />
            <Tile label="Difference" value={sdeDiff != null ? `${sdeDiff >= 0 ? "+" : ""}${fmt$(sdeDiff)}` : "—"} tone={sdeDiff > 0 ? "pass" : "go"} />
            <Tile label="Add-back gap" value={sdeDiffPct != null ? `${sdeDiffPct >= 0 ? "+" : ""}${sdeDiffPct.toFixed(0)}%` : "—"} tone={sdeDiffPct > 15 ? "pass" : "go"} />
          </div>
          <table className="tbl">
            <thead><tr><th>Tax year</th><th className="num">Revenue</th><th className="num">Net income</th><th className="num">Tax SDE</th><th className="num">Tax EBITDA</th><th className="num">vs Seller SDE</th></tr></thead>
            <tbody>
              {allTaxYears.sort((a, b) => String(a.year).localeCompare(String(b.year))).map((y, i) => (
                <tr key={i} data-testid="tax-year-row">
                  <td>{y.year}</td><td className="num">{fmt$(y.revenue)}</td><td className="num">{fmt$(y.netIncome)}</td>
                  <td className="num"><b>{fmt$(y.taxSDE)}</b></td><td className="num">{fmt$(y.taxEBITDA)}</td>
                  <td className={`num ${M.sde - y.taxSDE > 0 ? "neg" : "pos"}`}>{M.sde - y.taxSDE >= 0 ? "+" : ""}{fmt$(M.sde - y.taxSDE)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {sdeDiffPct != null && sdeDiffPct > 15 && <div className="bullet" style={{ marginTop: 8, color: "var(--danger)" }}>⚠ Seller-adjusted SDE is {sdeDiffPct.toFixed(0)}% above tax-return SDE — scrutinize the add-backs during diligence.</div>}
        </Card>
      )}

      <div className="grid g2" style={{ marginTop: 12 }}>
        <Card title="Parsed Documents">
          {Object.entries(docs).length === 0 && <div style={{ fontSize: 12, color: "var(--text3)" }}>No documents loaded yet.</div>}
          {Object.entries(docs).map(([k, d]) => (
            <div className="kv-row" key={k}>
              <span className="k">{d.name}</span>
              <span>{d.status === "parsed" && <span className="pill go">Active</span>}</span>
            </div>
          ))}
        </Card>
        {fields && <PlatformLink compact />}
        <Card title="Extracted Data Preview" testid="extracted-preview">
          {!fields && <div style={{ fontSize: 12, color: "var(--text3)" }}>Upload a document to see extracted fields mapped into the model.</div>}
          {fields && (
            <div className="table-scroll">
              <table className="tbl">
                <thead><tr><th>Field</th><th className="num">Value</th><th>Source</th></tr></thead>
                <tbody>
                  {Object.entries(fields.mapped).filter(([k]) => k !== "historicalYears").map(([k, v]) => (
                    <tr key={k}><td>{k}</td><td className="num">{typeof v === "number" ? (["askPrice", "sde", "ttmRevenue", "reVal", "nwc", "ffe", "goodwill", "officerSalaryAddback"].includes(k) ? fmt$(v) : v) : String(v)}</td><td style={{ fontSize: 10, color: "var(--text3)" }}>{fields.source}</td></tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
